import { useEffect, useMemo, useState, useRef } from "react";
import { Pencil, Save, X } from "lucide-react";

import styles from "./editable-text-cell.module.scss";

export default function EditableTextAreaCell({
	value,
	row,
	fieldName = "observations",
	onSave,
	getRecordId = (originalRow) => originalRow?.id,
	displayFormatter,
	formatOutput = (val) => val,
	autoSaveOnBlur = false,
	disabled = false,
	className = "",
	readButtonTitle = "Editar observaciones",
	savingText = "Guardando…",
	cancelText = "Cancelar",
	saveText = "Guardar",
	expandOnEdit = true,
	placeholder = "",
	maxLength,
	minLength,
	required = false,
	rows = 3,
}) {
	const recordId = useMemo(() => getRecordId?.(row?.original), [getRecordId, row]);
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(value ?? "");
	const [isSaving, setIsSaving] = useState(false);
	const [errorText, setErrorText] = useState("");

	const textareaRef = useRef(null);
	const isMountedRef = useRef(false);
	const abortRef = useRef(null);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (abortRef.current) {
				try {
					abortRef.current.abort();
				} catch {
					// Ignore abort errors
				}
			}
		};
	}, []);

	useEffect(() => {
		if (!isEditing) setInputValue(value ?? "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		if (isEditing && textareaRef.current) {
			textareaRef.current.focus();
			textareaRef.current.select();
		}
	}, [isEditing]);

	const beginEdit = () => {
		if (disabled || isSaving) return;
		setErrorText("");
		setIsEditing(true);
	};

	const cancelEdit = () => {
		setInputValue(value ?? "");
		setErrorText("");
		setIsEditing(false);
	};

	const persistChange = async () => {
		if (disabled || isSaving) return;
		setErrorText("");

		const newValue = formatOutput(inputValue);

		if (required && !newValue) {
			setErrorText("Este campo es requerido");
			return;
		}

		if (!recordId || typeof onSave !== "function") {
			cancelEdit();
			return;
		}

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			setIsSaving(true);
			await onSave({
				recordId,
				originalRow: row?.original,
				fieldName,
				newValue,
				inputValue,
				signal: controller.signal,
			});
			if (!isMountedRef.current) return;
			setIsEditing(false);
		} catch (err) {
			if (!isMountedRef.current) return;
			if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
				setErrorText(err?.message || "No se pudo guardar");
			}
		} finally {
			if (isMountedRef.current) setIsSaving(false);
			abortRef.current = null;
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === "Escape") {
			event.preventDefault();
			cancelEdit();
		}
		// Note: We don't handle Enter for textarea to allow multi-line input
	};

	if (!isEditing) {
		const textToShow = typeof displayFormatter === "function" ? displayFormatter(value) : value ? String(value) : "—";
		return (
			<div className={`${styles.wrapper} ${className}`}>
				<button type="button" onClick={beginEdit} title={readButtonTitle} className={styles.readButton} disabled={disabled}>
					<span className={styles.readText}>{textToShow}</span>
					<Pencil className={styles.readIcon} aria-hidden="true" />
				</button>
			</div>
		);
	}

	return (
		<div className={`${styles.wrapper} ${className}`}>
			<div className={expandOnEdit ? styles.popover : styles.inlineEdit}>
				<textarea
					ref={textareaRef}
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={autoSaveOnBlur ? persistChange : undefined}
					disabled={isSaving || disabled}
					className={styles.input}
					placeholder={placeholder}
					maxLength={maxLength}
					minLength={minLength}
					required={required}
					rows={rows}
					aria-label="Editar observaciones"
				/>

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.saveButton}
						onMouseDown={(e) => e.preventDefault()}
						onClick={persistChange}
						disabled={isSaving || disabled}
					>
						<Save className={styles.actionIcon} aria-hidden="true" />
						<span>{isSaving ? savingText : saveText}</span>
					</button>

					<button type="button" className={styles.cancelButton} onMouseDown={(e) => e.preventDefault()} onClick={cancelEdit} disabled={isSaving}>
						<X className={styles.actionIcon} aria-hidden="true" />
						<span>{cancelText}</span>
					</button>
				</div>

				{errorText ? <div className={styles.errorText}>{errorText}</div> : null}
			</div>
		</div>
	);
}
